// AgencyLoginPage.js - Login page for agency agents with 2FA
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import SecurityLockoutMessage from './SecurityLockoutMessage';

const AgencyLoginPage = ({ agency, agencyCode, color = 'blue' }) => {
  const navigate = useNavigate();
  const { login: authLogin, setUser, checkAuth, user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // 2FA states
  const [show2FA, setShow2FA] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [userContact, setUserContact] = useState({ email: '', phone: '' });
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);

  const getColorClasses = () => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-600 hover:bg-blue-700',
        focus: 'focus:ring-blue-500 focus:border-blue-500',
        gradient: 'from-blue-50 to-blue-100',
        text: 'text-blue-600'
      },
      green: {
        bg: 'bg-green-600 hover:bg-green-700',
        focus: 'focus:ring-green-500 focus:border-green-500',
        gradient: 'from-green-50 to-green-100',
        text: 'text-green-600'
      },
      purple: {
        bg: 'bg-purple-600 hover:bg-purple-700',
        focus: 'focus:ring-purple-500 focus:border-purple-500',
        gradient: 'from-purple-50 to-purple-100',
        text: 'text-purple-600'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  const colors = getColorClasses();

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Step 1: Initial login attempt
        const response = await authService.login({
          email: formData.email,
          password: formData.password,
          agencyCode: agencyCode
        });
        
        // If 2FA is required, the backend will return a temp token
        if (response.data.data.requiresTwoFactor) {
          setTempToken(response.data.data.tempToken);
          setUserContact({
            email: response.data.data.maskedEmail || formData.email,
            phone: response.data.data.maskedPhone || ''
          });
          setShow2FA(true);
          setLoading(false);
          
          // Don't send initial 2FA code automatically - let user choose method first
        } else {
          // No 2FA required, proceed with login
          await handleLoginSuccess(response.data.data);
        }
      } else {
        // Redirect to agency registration
        navigate(`/${agencyCode.toLowerCase()}/register`);
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      // Handle security lockout responses
      if (errorData?.type === 'account_locked' || errorData?.type === 'ip_rate_limited') {
        setError(errorData);
      } else if (errorData?.remainingAttempts !== undefined) {
        // Show failed attempts warning
        setError({
          ...errorData,
          type: 'failed_attempts_warning'
        });
      } else {
        setError(errorData?.message || 'Invalid credentials');
      }
      setLoading(false);
    }
  };

  const send2FACode = async (selectedMethod = verificationMethod) => {
    setSendingCode(true);
    setError('');
    try {
      await authService.send2FACode({
        tempToken,
        method: selectedMethod
      });
      setCodeSent(true);
      startResendTimer();
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.verify2FA({
        tempToken,
        code: verificationCode
      });

      // Set user directly from 2FA response instead of calling checkAuth
      const userData = response.data.user;
      console.log('🔍 2FA Success - User data received:', userData);
      
      // Clear any existing auth state to prevent cache issues
      localStorage.removeItem('user');
      
      // Set user in context
      setUser(userData);
      console.log('🔍 2FA Success - User set in context');
      
      // Force a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));

      // DEBUG: Log user data to see what we're working with
      console.log('🔍 2FA Success - userData.role:', userData.role);
      console.log('🔍 2FA Success - userData.status:', userData.status);
      console.log('🔍 2FA Success - Full userData:', JSON.stringify(userData));

      // Check for commission-only agents first
      if (userData.role === 'agent' && userData.status === 'commission_only') {
        console.log('🔍 2FA Success - Commission-only agent detected, redirecting to commissions-only page');
        navigate('/agent/commissions-only', { replace: true });
        return;
      }

      // Navigate based on user role with cache busting
      const targetPath = userData.role === 'agency_admin' ? '/agency/dashboard' :
                        userData.role === 'agent' ? '/agent/dashboard' :
                        userData.role === 'super_admin' ? '/admin/dashboard' :
                        '/agent/dashboard';
      
      console.log('🔍 2FA Success - Navigating to:', targetPath);
      navigate(targetPath, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = async (data) => {
    try {
      console.log('Login success data received:', data);
      
      // Store auth data properly
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        console.log('Token and user data stored successfully');
      } else {
        console.error('No token in response data:', data);
        throw new Error('No authentication token received');
      }
      
      // Use checkAuth to properly set the user state and ensure synchronization
      await checkAuth();
      
      // Wait a moment for React state to update, then use the user from context
      setTimeout(() => {
        // Access user from the context which has been updated by checkAuth
        const currentUser = user || data.user;
        
        console.log('Navigating based on user role:', currentUser.role, 'status:', currentUser.status);
        
        // Check for commission-only agents first
        if (currentUser.role === 'agent' && currentUser.status === 'commission_only') {
          console.log('Commission-only agent detected, redirecting to commissions-only page');
          navigate('/agent/commissions-only');
          return;
        }
        
        // Navigate based on user role
        if (currentUser.role === 'agency_admin') {
          navigate('/agency/dashboard');
        } else if (currentUser.role === 'agent') {
          navigate('/agent/dashboard');
        } else if (currentUser.role === 'super_admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/agent/dashboard'); // Default to agent dashboard
        }
      }, 200);
    } catch (error) {
      console.error('Error in handleLoginSuccess:', error);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  const resend2FA = async () => {
    if (resendTimer > 0 || sendingCode) return;
    
    try {
      await send2FACode();
      setError('');
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    }
  };

  if (show2FA) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${colors.gradient} py-12 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="text-center">
              <div className={`mx-auto h-20 w-20 ${colors.bg} rounded-full flex items-center justify-center mb-4`}>
                <span className="text-3xl font-bold text-white">{agencyCode}</span>
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900">
                Two-Factor Authentication
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {!codeSent ? 
                  'Select a delivery method below to send your verification code:' :
                  sendingCode ? 
                    'Sending verification code...' :
                    `Verification code sent to your ${verificationMethod === 'email' ? 'email' : 'phone'}`
                }
              </p>
            </div>
          </div>

          <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleVerify2FA} className="space-y-6">
              <div className="text-center mb-6">
                {!codeSent ? (
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Choose your preferred delivery method:
                    </p>
                    <div className="flex justify-center space-x-4">
                      <button
                        type="button"
                        onClick={async () => {
                          setVerificationMethod('email');
                          await send2FACode('email');
                        }}
                        className={`px-6 py-3 rounded-lg text-sm font-medium border-2 transition-all duration-200 ${
                          sendingCode && verificationMethod === 'email'
                            ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                            : `border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`
                        }`}
                        disabled={sendingCode}
                      >
                        {sendingCode && verificationMethod === 'email' ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Send to Email
                          </div>
                        )}
                      </button>
                      {userContact.phone && (
                        <button
                          type="button"
                          onClick={async () => {
                            setVerificationMethod('sms');
                            await send2FACode('sms');
                          }}
                          className={`px-6 py-3 rounded-lg text-sm font-medium border-2 transition-all duration-200 ${
                            sendingCode && verificationMethod === 'sms'
                              ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                              : `border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500`
                          }`}
                          disabled={sendingCode}
                        >
                          {sendingCode && verificationMethod === 'sms' ? (
                            <div className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending...
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              Send to Phone
                            </div>
                          )}
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-3">
                      <p>📧 Email: {userContact.email}</p>
                      {userContact.phone && <p>📱 Phone: {userContact.phone}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center text-green-600 mb-2">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm font-medium">Code Sent Successfully!</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {verificationMethod === 'email' 
                        ? `Verification code sent to: ${userContact.email}`
                        : `Verification code sent to: ${userContact.phone}`
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      Enter the 6-digit code below to continue
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  maxLength="6"
                  className={`mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${colors.focus} sm:text-sm text-center text-2xl tracking-widest`}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                />
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={resend2FA}
                  disabled={resendTimer > 0 || sendingCode}
                  className={`text-sm transition-colors duration-200 ${
                    resendTimer > 0 || sendingCode 
                      ? 'text-gray-400 cursor-not-allowed' 
                      : colors.text + ' hover:opacity-80 hover:underline'
                  }`}
                >
                  {sendingCode ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </div>
                  ) : resendTimer > 0 ? (
                    `Resend code in ${resendTimer}s`
                  ) : (
                    'Resend verification code'
                  )}
                </button>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${colors.bg} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-500 disabled:opacity-50`}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShow2FA(false);
                    setVerificationCode('');
                    setError('');
                    setCodeSent(false);
                    setFormData({ email: '', password: '' });
                    setTempToken('');
                    setSendingCode(false);
                    setLoading(false);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  ← Back to login
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${colors.gradient} py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="text-center">
            <div className={`mx-auto h-20 w-20 ${colors.bg} rounded-full flex items-center justify-center mb-4`}>
              <span className="text-3xl font-bold text-white">{agencyCode}</span>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {agency} Agent Portal
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {isLogin ? 'Sign in to access your dashboard' : 'Apply to join our agency'}
            </p>
          </div>
        </div>

        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
          {error && typeof error === 'object' && error.type ? (
            <SecurityLockoutMessage 
              error={error}
              onRetry={() => {
                setError('');
                setLoading(false);
              }}
              onForgotPassword={() => {
                navigate(`/${agencyCode.toLowerCase()}/password-reset`);
              }}
            />
          ) : error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {isLogin ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${colors.focus} sm:text-sm`}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className={`appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${colors.focus} sm:text-sm`}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {showPassword ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className={`h-4 w-4 ${colors.text} focus:ring-${color}-500 border-gray-300 rounded`}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <button
                    type="button"
                    onClick={() => navigate(`/${agencyCode.toLowerCase()}/forgot-password`)}
                    className={`font-medium ${colors.text} hover:opacity-80`}
                  >
                    Forgot your password?
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-600 text-center">
                Two-factor authentication is enabled for your security
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${colors.bg} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-500 disabled:opacity-50`}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <p className="mb-6 text-gray-600">
                To apply to join {agency}, please click the button below to access our application form.
              </p>
              <button
                onClick={() => window.location.href = `/${agencyCode.toLowerCase()}/register`}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${colors.bg} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-500`}
              >
                Apply to Join {agency}
              </button>
            </div>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {isLogin ? "New to " + agency + "?" : "Already an agent?"}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {isLogin ? 'Apply to join ' + agency : 'Sign in instead'}
              </button>
            </div>
          </div>
        </div>

        <div className="text-center space-y-2">
          <div>
            <a href={`/${agencyCode.toLowerCase()}/admin`} className={`text-sm ${colors.text} hover:opacity-80`}>
              Agency Admin Login →
            </a>
          </div>
          <div>
            <a href="/" className="text-sm text-gray-600 hover:text-gray-900">
              ← Back to home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyLoginPage;